import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { FormApi, FormState } from "final-form";
import { capitalize, debounce } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { Field, Form, FormSpy, useForm } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Descendant } from "slate";

import { ArrowBack } from "@livechat/design-system-icons";
import {
  AutoComplete,
  Button,
  DesignToken,
  FormField,
  Heading,
  IPickerListItem,
  Icon,
  Input,
  Picker,
  ShadowToken,
  SpacingToken,
  Tooltip,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { PriorityPickerListItem } from "components/picker-items/priorityPickerItems";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { TagPicker } from "components/tags";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { getPathForRoute } from "constants/tickets/TicketsRoutes";
import { NEW_TICKET_TEMP_ID } from "constants/tickets/drafts";
import { getFilteredTags, mapTags } from "helpers/tags";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useRedirectToTextApp } from "hooks/use-redirect-to-text-app";
import { useDebouncedAddresses } from "hooks/useDebounceGetAddresses";
import { TicketPriority, TicketStatus } from "interfaces/entities/ticket";
import {
  IAgent,
  IWithAgentsSessionState,
  IWithAgentsState,
  selectAgentsAlphabetically,
  selectLoggedInAgent,
} from "store/entities/agents";
import { selectAgentCannedResponsesByShortcuts } from "store/entities/canned-responses";
import { selectDefaultTeamId, selectLicenseId } from "store/entities/license";
import { selectReplyAddresses } from "store/entities/reply-addresses/selectors";
import { selectAgentTagsAlphabetically, selectTagsAlphabetically } from "store/entities/tags";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getRoute, isSendingMessage, selectCanManageTickets } from "store/views/tickets/selectors";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import TicketMessageBox from "../ticket/TicketMessageBox";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import AttachmentProvider from "../ticket/message-box/richtext/AttachmentProvider";

import { PeopleInTheLoopPicker } from "./PeopleInTheLoopPicker";
import {
  ICreateTicketFormValues,
  getInitialCreateTicketFormValues,
  getTeamReplyAddress,
  scrollToFirstError,
  validateTicketAddRequesterEmail,
  validateTicketAddSubject,
} from "./formUtils";

const UNASSIGNED_OPTION_KEY = "unassigned";

const getTeamPickerListItem = (team: ITeam, replyAddress?: string): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    listItemBody: (
      <TeamPickerListItem
        name={team.name}
        secondaryText={replyAddress}
        isDefault={team.isDefault}
        pickerType="single"
      />
    ),
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} secondaryText={replyAddress} pickerType="single" />,
  },
});

const getAgentPickerListItem = (agent: IAgent, loggedInAgentId?: string): IPickerListItem => ({
  key: agent.id,
  name: agent.name,
  customElement: {
    listItemBody: (
      <AgentPickerListItem
        agentName={agent.name}
        secondaryText={agent.email}
        avatarUrl={agent.avatar}
        isLoggedInAgent={loggedInAgentId === agent.id}
      />
    ),
    selectedItemBody: (
      <AgentPickerSelectedListItem agentName={agent.name} secondaryText={agent.email} avatarUrl={agent.avatar} />
    ),
  },
});

const getUnassignedAgentPickerListItem = (): IPickerListItem => ({
  key: UNASSIGNED_OPTION_KEY,
  name: "Unassigned",
  customElement: {
    listItemBody: <AgentPickerListItem agentName="Unassigned" secondaryText="" avatarUrl="" />,
    selectedItemBody: <AgentPickerSelectedListItem agentName="Unassigned" avatarUrl="" />,
  },
});

const getPriorityPickerListItem = (priority: TicketPriority): IPickerListItem => ({
  key: priority.toString(),
  name: priority.toString(),
  customElement: {
    listItemBody: <PriorityPickerListItem priority={priority} />,
    selectedItemBody: <PriorityPickerListItem priority={priority} />,
  },
});

const getStatusPickerListItem = (status: TicketStatus): IPickerListItem => ({
  key: status,
  name: capitalize(status),
});

const TicketAddComponent: FC<IWithBreakpointsProps> = ({ isMobile }) => {
  const { isFetching, isRedirecting } = useRedirectToTextApp("/inbox/tickets");

  const history = useHistory();
  const dispatch = useDispatch();

  const ticketsRoute = useSelector(getRoute);
  const teams = useSelector(selectTeamsAlphabetically);
  const tags = useSelector(selectTagsAlphabetically);
  const agentTags = useSelector(selectAgentTagsAlphabetically);
  const defaultTeamId = useSelector(selectDefaultTeamId);
  const activeAgentsWithoutViewers = useSelector((state: IWithAgentsState & IWithAgentsSessionState) =>
    selectAgentsAlphabetically(state, {
      activeOnly: true,
      excludeViewers: true,
      loggedInAgentOnTop: true,
    })
  );
  const replyAddresses = useSelector(selectReplyAddresses);
  const licenseId = useSelector(selectLicenseId);
  const loggedInAgent = useSelector(selectLoggedInAgent);
  const canManageTickets = useSelector(selectCanManageTickets);
  const sendingMessage = useSelector(isSendingMessage);
  const cannedResponsesByShortcuts = useSelector(selectAgentCannedResponsesByShortcuts);
  const currentAgent = useSelector(selectLoggedInAgent);

  const [transactionID, setTransactionID] = useState<string>("");
  const [isDebouncingActive, setIsDebouncingActive] = useState(true);

  const statusPickerItems: IPickerListItem[] = [
    TicketStatus.Open,
    TicketStatus.Pending,
    TicketStatus.OnHold,
    TicketStatus.Solved,
    TicketStatus.Closed,
  ].map(getStatusPickerListItem);

  const debouncedSaveDraft = useMemo(
    () =>
      debounce((formState: FormState<ICreateTicketFormValues>) => {
        if (isDebouncingActive && !formState.submitting && !sendingMessage) {
          dispatch(
            TicketsViewActions.saveDraft({
              ...formState.values,
              ticketId: NEW_TICKET_TEMP_ID,
            })
          );
        }
      }, 500),
    [isDebouncingActive, dispatch]
  );

  const handleBackToList = (): void => {
    history.push(getPathForRoute(ticketsRoute));
  };

  const handleTransactionChange = (transactionId: string): void => {
    setTransactionID(transactionId);
  };

  const handleFormSubmit = (values: ICreateTicketFormValues): void => {
    debouncedSaveDraft.flush();
    debouncedSaveDraft.cancel();

    setIsDebouncingActive(false);

    const {
      subject,
      requesterName,
      requesterEmail,
      assigneeId,
      assignedTeamId,
      priority,
      status,
      tags,
      isPrivate,
      richTextPresent,
      message,
      transactionID,
      cannedResponseIds,
      peopleInTheLoop,
    } = values;

    dispatch(
      TicketsViewActions.createTicket({
        requester: {
          name: requesterName.trim(),
          mail: requesterEmail.trim(),
        },
        richTextPresent,
        subject: subject.trim(),
        priority: parseInt(priority, 10),
        assigneeId,
        assignedTeamId,
        message,
        cannedResponseIds,
        isPrivate,
        transactionID,
        status,
        tags,
        peopleInTheLoop,
      })
    );
  };

  const handleMessageBoxDraftSave =
    (form: FormApi<ICreateTicketFormValues, Partial<ICreateTicketFormValues>>) =>
    (
      message: Descendant[],
      _messageText: string,
      _isPrivate: boolean,
      _status: TicketStatus,
      cannedResponseIds: string[]
    ): void => {
      form.change("message", message);
      form.change("cannedResponseIds", cannedResponseIds);
    };

  useEffect(() => {
    return (): void => {
      debouncedSaveDraft.cancel();
    };
  }, []);

  if (isFetching || isRedirecting) {
    return null;
  }

  return (
    // TODO: Remove theme provider once tag picker and message box gets updated

    <ThemeProvider theme={legacyTheme}>
      <Container>
        <Header>
          <Button
            size="compact"
            kind="plain"
            onClick={handleBackToList}
            icon={<Icon source={ArrowBack} />}
            id="backButton"
          />
          <Heading bold={false} customColor={withVar(DesignToken.ContentBasicPrimary)}>
            New ticket
          </Heading>
        </Header>
        <Form
          keepDirtyOnReinitialize
          initialValues={getInitialCreateTicketFormValues(defaultTeamId, activeAgentsWithoutViewers, teams)}
          onSubmit={handleFormSubmit}
          render={({ handleSubmit, values, form, errors }) => {
            const handleMessageSend = (
              message: Descendant[],
              isPrivate: boolean,
              _status: TicketStatus,
              _attachments?: File[],
              transactionID?: string,
              richTextPresent?: boolean,
              _mentionedAgents?: string[],
              _handleClearMessageBox?: () => void,
              _usingShortcut?: boolean,
              cannedResponseIds?: string[]
            ): void => {
              form.change("message", message);
              form.change("isPrivate", isPrivate);
              form.change("transactionID", transactionID);
              form.change("richTextPresent", richTextPresent);
              form.change("cannedResponseIds", cannedResponseIds);
              form.submit();

              if (errors) {
                scrollToFirstError(errors);
                return;
              }
            };

            const disableAssignToMeButton = !loggedInAgent.teamIds.includes(values.assignedTeamId);

            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const teamPickerItems: IPickerListItem[] = teams.map((team: any) => {
              const replyAddress = getTeamReplyAddress(team, replyAddresses, licenseId);
              return getTeamPickerListItem(team, replyAddress);
            });

            const agentPickerItems: IPickerListItem[] = [
              getUnassignedAgentPickerListItem(),
              ...activeAgentsWithoutViewers // FIXME: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .filter((agent: any) => agent.teamIds.includes(values.assignedTeamId)) // FIXME: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((agent: any) => getAgentPickerListItem(agent, loggedInAgent.id)),
            ];

            return (
              <form onSubmit={handleSubmit}>
                <FormSpy subscription={{ values: true, submitting: true }} onChange={debouncedSaveDraft} />
                <Content>
                  <Field
                    name="subject"
                    validate={validateTicketAddSubject}
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    render={({ input, meta: { touched, error } }: any) => (
                      <FormFieldWithMargin labelText="Subject" error={touched && error}>
                        <Input id="subject" error={touched && error} placeholder="Enter subject" {...input} />
                      </FormFieldWithMargin>
                    )}
                  />

                  <RequesterFields>
                    <Field
                      name="requesterName"
                      // FIXME: Fix this the next time the file is edited.
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      render={({ input }: any) => (
                        <FormFieldWithMargin labelText="Requester name" requesterField>
                          <Input
                            placeholder="Enter requester name"
                            data-1p-ignore
                            autoComplete="off"
                            data-form-type="other"
                            id="requesterName"
                            {...input}
                          />
                        </FormFieldWithMargin>
                      )}
                    />
                    <RequesterEmailField peopleInTheLoop={values.peopleInTheLoop} />
                  </RequesterFields>

                  <PeopleInTheLoopPicker requesterEmail={values.requesterEmail} />

                  <Field
                    name="assignedTeamId"
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    render={({ input }: any) => (
                      <FormFieldWithMargin labelText="Team">
                        <Picker
                          hideClearButton
                          placeholder="Select team"
                          options={teamPickerItems}
                          selected={teamPickerItems.filter(team => team.key === values.assignedTeamId)}
                          // FIXME: Fix this the next time the file is edited.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onSelect={(selected: any) => {
                            input.onChange(selected ? selected[0].key : "");
                            form.change("assigneeId", "");
                          }}
                          maxListHeight={250}
                          data-testid="team-picker"
                          clearSearchAfterSelection={false}
                        />
                      </FormFieldWithMargin>
                    )}
                  />

                  <Field
                    name="assigneeId"
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    render={({ input }: any) => (
                      <FormFieldWithMargin
                        labelText="Agent"
                        labelRightNode={
                          <Tooltip
                            kind="invert"
                            placement="top"
                            isVisible={disableAssignToMeButton ? undefined : false}
                            triggerRenderer={
                              <Button
                                id="ticket-assign-agent-button"
                                size="xcompact"
                                kind="link"
                                onClick={() => {
                                  input.onChange(values.assigneeId === loggedInAgent.id ? "" : loggedInAgent.id);
                                }}
                                disabled={disableAssignToMeButton}
                              >
                                {values.assigneeId === loggedInAgent.id ? "Unassign me" : "Assign me"}
                              </Button>
                            }
                          >
                            You aren&apos;t added to the selected team.
                          </Tooltip>
                        }
                      >
                        <Picker
                          hideClearButton
                          placeholder="Select agent"
                          options={agentPickerItems}
                          selected={agentPickerItems.filter(agent => {
                            const inputValue = input.value || UNASSIGNED_OPTION_KEY;

                            return agent.key === inputValue;
                          })}
                          // FIXME: Fix this the next time the file is edited.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onSelect={(selected: any) => {
                            input.onChange(
                              selected ? (selected[0].key === UNASSIGNED_OPTION_KEY ? "" : selected[0].key) : ""
                            );
                          }}
                          maxListHeight={240}
                          data-testid="agent-picker"
                          clearSearchAfterSelection={false}
                        />
                      </FormFieldWithMargin>
                    )}
                  />

                  <Field
                    name="priority"
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    render={({ input }: any) => (
                      <FormFieldWithMargin labelText="Priority">
                        <Picker
                          id="priority-select"
                          searchDisabled
                          hideClearButton
                          placeholder="Select priority"
                          options={[
                            TicketPriority.Low,
                            TicketPriority.Medium,
                            TicketPriority.High,
                            TicketPriority.Urgent,
                          ].map(getPriorityPickerListItem)}
                          selected={[getPriorityPickerListItem(parseInt(input.value, 10) as TicketPriority)]}
                          // FIXME: Fix this the next time the file is edited.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onSelect={(selected: any) => {
                            input.onChange(selected ? selected[0].key : TicketPriority.Medium.toString());
                          }}
                        />
                      </FormFieldWithMargin>
                    )}
                  />

                  <Field
                    name="status"
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    render={({ input }: any) => (
                      <FormFieldWithMargin labelText="Status">
                        <Picker
                          searchDisabled
                          hideClearButton
                          placeholder="Select status"
                          options={statusPickerItems}
                          selected={[getStatusPickerListItem(input.value as TicketStatus)]}
                          // FIXME: Fix this the next time the file is edited.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onSelect={(selected: any) => {
                            input.onChange(selected ? selected[0].key : TicketStatus.Pending);
                          }}
                        />
                      </FormFieldWithMargin>
                    )}
                  />

                  <Field
                    name="tags"
                    render={({ input }) => (
                      <Block marginBottom={withVar(isMobile ? SpacingToken.Spacing3 : SpacingToken.Spacing5)}>
                        <TagPicker
                          options={mapTags(agentTags)}
                          selectedTags={getFilteredTags(tags, input.value)}
                          canManageTags={canManageTickets}
                          onSelect={tagId => input.onChange([...input.value, tagId])}
                          onRemove={tagId => input.onChange(input.value.filter((tag: string) => tag !== tagId))}
                          teamID={values.assignedTeamId}
                          ticketID={NEW_TICKET_TEMP_ID}
                          disableShortcuts
                          disableTagReminder
                        />
                      </Block>
                    )}
                  />

                  <AttachmentProvider onTransactionChange={handleTransactionChange} ephemeralID={NEW_TICKET_TEMP_ID}>
                    <TicketMessageBox
                      blockEditForMerged={false}
                      agentSignature={currentAgent.signature}
                      agentId={currentAgent.id}
                      cannedResponsesByShortcuts={cannedResponsesByShortcuts}
                      canSendPublicMessage={canManageTickets}
                      canSendPrivateMessage={canManageTickets}
                      isSendingMessage={sendingMessage}
                      newTicket
                      transactionID={transactionID}
                      onMessageSend={handleMessageSend}
                      onDraftSave={handleMessageBoxDraftSave(form)}
                    />
                  </AttachmentProvider>
                </Content>
              </form>
            );
          }}
        />
      </Container>
    </ThemeProvider>
  );
};

const RequesterEmailField: FC<{ peopleInTheLoop: string[] }> = ({ peopleInTheLoop }) => {
  const form = useForm();

  const validator = useMemo(() => validateTicketAddRequesterEmail(peopleInTheLoop), [peopleInTheLoop]);

  return (
    <Field<string> name="requesterEmail" validate={validator}>
      {({ input, meta }) => {
        const addresses = useDebouncedAddresses("requester", input.value);

        const filteredAddresses = addresses?.filter(address => !validator(address));
        return (
          <FormFieldWithMargin labelText="Requester email" error={meta.touched && meta.error} requesterField>
            <AutoComplete
              options={filteredAddresses}
              id="requesterEmail"
              maxLength={1000}
              error={meta.touched && meta.error}
              className="fs-mask"
              placeholder="Enter requester email address"
              data-1p-ignore
              autoComplete="off"
              {...input}
              onChange={e => {
                input.onChange(e);
                form.resetFieldState("requesterEmail");
              }}
            />
          </FormFieldWithMargin>
        );
      }}
    </Field>
  );
};

const Header = styled(Flex)`
  min-height: 52px;
  align-items: center;
  box-shadow: ${withVar(ShadowToken.DividerBottom)};
  padding: 0 ${withVar(SpacingToken.Spacing3)};
  gap: ${withVar(SpacingToken.Spacing2)};
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  position: sticky;
  top: 0;
  z-index: 1; // above new ticket form, below product switcher
`;

const Content = styled(Flex)`
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  margin: auto;
  max-width: 680px;
  width: 100%;
  padding: ${withVar(SpacingToken.Spacing12)} ${withVar(SpacingToken.Spacing5)};
  box-sizing: border-box;

  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    padding: ${withVar(SpacingToken.Spacing3)};
  }
`;

const Container = styled(Flex)`
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const FormFieldWithMargin = styled(FormField)<{ requesterField?: boolean; hideLabel?: boolean }>`
  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ requesterField }: any) => requesterField && "width: 100%;"
  }
  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ hideLabel }: any) => hideLabel && "margin-top: 24px;"
  }

  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    margin-bottom: ${withVar(SpacingToken.Spacing3)};
    margin-top: 0;
  }
`;

const RequesterFields = styled(Flex)`
  gap: ${withVar(SpacingToken.Spacing2)};
  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    gap: 0;
    flex-direction: column;
  }
`;

export const TicketAdd = withBreakpoints(TicketAddComponent);
